import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { DocumentRequestDetailsDialog } from '@/features/preparer/tax-binders/DocumentRequestDetailsDialog';
import { documentRequestQueryOptions } from '@/lib/query/options';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';
import { DocumentRequestTab } from '@/types/documentRequestTabs';
import { TaxBinderTab } from '@/types/taxBinderTabs';

const documentRequestSearchSchema = z.object({
  requestTab: z
    .enum([
      DocumentRequestTab.Overview,
      DocumentRequestTab.Documents,
      DocumentRequestTab.Comments,
      DocumentRequestTab.History,
    ])
    .optional()
    .catch(DocumentRequestTab.Overview),
  fromTab: z
    .enum([
      TaxBinderTab.DocumentRequests,
      TaxBinderTab.ClientDocuments,
      TaxBinderTab.FirmDocuments,
      TaxBinderTab.Questionnaire,
      TaxBinderTab.Comments,
      TaxBinderTab.InternalNotes,
      TaxBinderTab.AuditLog,
      TaxBinderTab.TaxReturns,
      TaxBinderTab.Signatures,
      TaxBinderTab.K1Distribution,
    ])
    .optional()
    .catch(TaxBinderTab.DocumentRequests),
});

export const Route = createFileRoute('/preparer/tax-binders/$binderId/document-request/$requestId')({
  validateSearch: documentRequestSearchSchema,
  loader: async ({ context: { queryClient }, params: { requestId } }) => {
    // Get auth state from query cache
    const authState = queryClient.getQueryData(['auth', 'session']) as { token?: string } | undefined;
    const token = authState?.token;

    if (token && requestId) {
      // Prefetch document request details - critical data
      await queryClient.ensureQueryData(documentRequestQueryOptions.detail(requestId, token));
    }
  },
  errorComponent: QueryErrorBoundary,
  component: DocumentRequestRoute,
});

function DocumentRequestRoute() {
  const navigate = Route.useNavigate();
  const { binderId, requestId } = Route.useParams();
  const { requestTab, fromTab } = Route.useSearch();

  const handleClose = () => {
    navigate({
      to: '/preparer/tax-binders/$binderId',
      params: { binderId },
      search: { tab: fromTab || TaxBinderTab.DocumentRequests },
    });
  };

  const handleUpdate = () => {
    // Optional: Could trigger a refetch of binder data
    // For now, navigation back will reload the binder page
  };

  return (
    <DocumentRequestDetailsDialog
      open={true}
      onClose={handleClose}
      documentRequestId={requestId}
      binderId={binderId}
      onUpdate={handleUpdate}
      initialTab={requestTab}
      fromTab={fromTab}
    />
  );
}
